import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import VoordeurOptionsPanel from '../components/VoordeurOptionsPanel';
import DoorDrawingBoard from '../components/DoorDrawingBoard';
import Summary from '../components/Summary';
import ConfiguratorShell from '../components/ConfiguratorShell';
import { useTranslation } from '../context/TranslationContext';
import { 
  profileSystems,
  glassTextures, 
  glazingTypes, 
  colors,
  woodJointOptions,
  PRICING 
} from '../data/mockData';
import { useConfiguratorLoadState } from '../hooks/useConfiguratorLoadState';
import {
  applyDesignTypeChange,
  applyDoorTypeChange,
  VOORDEUR_DOOR_LEAF_MM,
  VOORDEUR_TOP_LIGHT_HEIGHT_MM,
  VOORDEUR_SIDE_PANEL_MM,
  DOOR_NO_PANEL_MAX_WIDTH,
  DOOR_MAX_TOTAL_WIDTH,
  DOOR_MAX_DOOR_LEAF_WIDTH,
  DOOR_MIN_DOOR_HEIGHT_MM,
  DOOR_NORMAL_MAX_HEIGHT,
  DOOR_MAX_DOOR_HEIGHT,
  DOOR_MIN_OVERHEAD_HEIGHT,
  DOOR_MAX_TOTAL_HEIGHT,
  getDoorMinTotalWidth,
  enforceDoorWidthBounds,
  enforceDoorHeightBounds,
} from '../lib/voordeurConfigHelpers';
import {
  applyDoorLayoutFieldChange,
} from '../lib/partWidthDistribution';
import {
  applyPersistedFrameColors,
  trackFrameColorChange,
  DOOR_COLOR_FIELDS,
} from '../lib/configuratorColors';
import {
  createDefaultAdditionalItems,
  withDefaultAdditionalItems,
  applyAdditionalItemsChange,
} from '../lib/additionalItems';

const VOORDEUR_MIN_SIDE_PANEL_WIDTH = VOORDEUR_SIDE_PANEL_MM;
const VOORDEUR_MIN_DOOR_LEAF_WIDTH = 700;
const VOORDEUR_MIN_TOTAL_HEIGHT = DOOR_MIN_DOOR_HEIGHT_MM;

const DEFAULT_VOORDEUR_CONFIG = {
  width: 1000,
  height: 2200,
  doorStyle: 'style-1',
  designType: 'no-panel',
  doorType: 'normal',
  numberOfParts: 3,
  thresholdHeight: '30mm',
  handlePosition: 'left',
  parapet: 'rvs-bar',
  security: 'skg-2',
  profileSystem: 'schuco',
  glassTexture: 'helder-glas',
  glazingType: 'double',
  woodJoint: 'joint-45',
  interiorColor: 'white',
  exteriorColor: 'anthracite',
  quantity: 1,
  overheadParts: 1,
  overheadPartWindowTypes: ['fixed-sash'],
  overheadHeight: undefined,
  leftPanelWidth: undefined,
  rightPanelWidth: undefined,
  leftPanelWindowType: 'fixed-sash',
  rightPanelWindowType: 'fixed-sash',
  leftPanelParapet: false,
  rightPanelParapet: false,
  leftPanelParapetHeight: 250,
  rightPanelParapetHeight: 250,
  leftPanelParapetColor: undefined,
  rightPanelParapetColor: undefined,
  leftPanelParapetPanelType: undefined,
  rightPanelParapetPanelType: undefined
};

const Voordeur = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const drawingBoardRef = useRef(null);
  
  const { config, setConfig } = useConfiguratorLoadState(
    location,
    () =>
      withDefaultAdditionalItems(
        applyPersistedFrameColors(DEFAULT_VOORDEUR_CONFIG, DOOR_COLOR_FIELDS)
      ),
    (loaded) =>
      withDefaultAdditionalItems(applyPersistedFrameColors(loaded, DOOR_COLOR_FIELDS))
  );

  const handleReset = () => {
    setConfig(withDefaultAdditionalItems({ ...DEFAULT_VOORDEUR_CONFIG }));
  };

  const handleAdditionalItemsChange = (updater) => {
    setConfig((prev) => applyAdditionalItemsChange(prev, updater));
  };

  const handleConfigChange = (field, value) => {
    trackFrameColorChange(field, value, DOOR_COLOR_FIELDS);
    setConfig((prev) => {
      if (field === 'designType') {
        return enforceDoorWidthBounds({ ...prev, ...applyDesignTypeChange(prev, value) });
      }
      if (field === 'doorType') {
        return { ...prev, ...applyDoorTypeChange(prev, value) };
      }
      if (field === 'doorStyle') {
        const next = { ...prev, doorStyle: value };
        if (value === 'style-6') {
          next.style6Layout = Number(prev.numberOfParts) === 4 ? 'k' : 'm';
        }
        return next;
      }
      if (field === 'numberOfParts') {
        const parts = Number(value);
        if (prev.doorStyle === 'style-6') {
          return {
            ...prev,
            numberOfParts: parts,
            style6Layout: parts === 4 ? 'k' : 'm',
          };
        }
        return { ...prev, numberOfParts: parts };
      }

      if (field === 'width') {
        const numericWidth = Math.round(Number(value) || prev.width);
        const currentDesign = prev.designType || 'no-panel';
        if (
          numericWidth > DOOR_NO_PANEL_MAX_WIDTH &&
          currentDesign === 'no-panel'
        ) {
          return enforceDoorWidthBounds({
            ...prev,
            designType: 'left-panel',
            width: numericWidth,
            leftPanelWidth: Math.max(
              VOORDEUR_MIN_SIDE_PANEL_WIDTH,
              numericWidth - VOORDEUR_DOOR_LEAF_MM
            ),
            rightPanelWidth: undefined,
          });
        }
        if (
          numericWidth <= DOOR_NO_PANEL_MAX_WIDTH &&
          currentDesign !== 'no-panel'
        ) {
          return {
            ...prev,
            designType: 'no-panel',
            width: numericWidth,
            leftPanelWidth: undefined,
            rightPanelWidth: undefined,
          };
        }
      }

      if (field === 'height') {
        const numericHeight = Math.round(Number(value) || prev.height);
        if (numericHeight > DOOR_NORMAL_MAX_HEIGHT) {
          return enforceDoorHeightBounds({
            ...prev,
            doorType: 'top-light',
            height: numericHeight,
            overheadHeight: prev.overheadHeight ?? VOORDEUR_TOP_LIGHT_HEIGHT_MM,
          });
        }
        if (prev.doorType === 'top-light') {
          return {
            ...prev,
            doorType: 'normal',
            height: numericHeight,
          };
        }
        return { ...prev, height: numericHeight };
      }

      if (field === 'overheadHeight') {
        return enforceDoorHeightBounds({
          ...prev,
          overheadHeight: Math.round(Number(value) || prev.overheadHeight || VOORDEUR_TOP_LIGHT_HEIGHT_MM),
        });
      }

      const layoutUpdate = applyDoorLayoutFieldChange(prev, field, value);
      if (layoutUpdate) {
        return enforceDoorWidthBounds({ ...prev, ...layoutUpdate });
      }

      return { ...prev, [field]: value };
    });
  };

  useEffect(() => {
    if (location.pathname !== '/voordeur') {
      navigate('/configurator-select', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <ConfiguratorShell
      title={t('configurator.frontDoorConfigurator')}
      drawingBoardRef={drawingBoardRef}
      onReset={handleReset}
      previewCentered
      optionsPanel={
        <VoordeurOptionsPanel
          config={config}
          onConfigChange={handleConfigChange}
          additionalItems={config.additionalItems || createDefaultAdditionalItems()}
          onAdditionalItemsChange={handleAdditionalItemsChange}
        />
      }
      previewPanel={
        <DoorDrawingBoard
          width={config.width}
          height={config.height}
          doorStyle={config.doorStyle}
          designType={config.designType}
          doorType={config.doorType}
          numberOfParts={config.numberOfParts ?? 3}
          onNumberOfPartsChange={(value) => handleConfigChange('numberOfParts', value)}
          handlePosition={config.handlePosition}
          interiorColor={config.interiorColor}
          exteriorColor={config.exteriorColor}
          glassTexture={config.glassTexture}
          woodJoint={config.woodJoint || 'joint-90'}
          parapet={config.parapet}
          overheadPartWindowTypes={config.overheadPartWindowTypes}
          overheadHeight={config.overheadHeight}
          leftPanelWidth={config.leftPanelWidth}
          rightPanelWidth={config.rightPanelWidth}
          leftPanelWindowType={config.leftPanelWindowType}
          rightPanelWindowType={config.rightPanelWindowType}
          onOverheadPartWindowTypeChange={(index, value) => {
            const newTypes = [...(config.overheadPartWindowTypes || ['fixed-sash'])];
            newTypes[index] = value;
            handleConfigChange('overheadPartWindowTypes', newTypes);
          }}
          onOverheadHeightChange={(value) => handleConfigChange('overheadHeight', value)}
          onLeftPanelWidthChange={(value) => handleConfigChange('leftPanelWidth', value)}
          onRightPanelWidthChange={(value) => handleConfigChange('rightPanelWidth', value)}
          onDoorSectionWidthChange={(value) => handleConfigChange('doorSectionWidth', value)}
          onLeftPanelWindowTypeChange={(value) => handleConfigChange('leftPanelWindowType', value)}
          onRightPanelWindowTypeChange={(value) => handleConfigChange('rightPanelWindowType', value)}
          leftPanelParapet={config.leftPanelParapet}
          rightPanelParapet={config.rightPanelParapet}
          leftPanelParapetHeight={config.leftPanelParapetHeight}
          rightPanelParapetHeight={config.rightPanelParapetHeight}
          leftPanelParapetColor={config.leftPanelParapetColor}
          rightPanelParapetColor={config.rightPanelParapetColor}
          leftPanelParapetPanelType={config.leftPanelParapetPanelType}
          rightPanelParapetPanelType={config.rightPanelParapetPanelType}
          onLeftPanelParapetChange={(value) => handleConfigChange('leftPanelParapet', value)}
          onRightPanelParapetChange={(value) => handleConfigChange('rightPanelParapet', value)}
          onLeftPanelParapetHeightChange={(value) => handleConfigChange('leftPanelParapetHeight', value)}
          onRightPanelParapetHeightChange={(value) => handleConfigChange('rightPanelParapetHeight', value)}
          onLeftPanelParapetColorChange={(value) => handleConfigChange('leftPanelParapetColor', value)}
          onRightPanelParapetColorChange={(value) => handleConfigChange('rightPanelParapetColor', value)}
          onLeftPanelParapetPanelTypeChange={(value) => handleConfigChange('leftPanelParapetPanelType', value)}
          onRightPanelParapetPanelTypeChange={(value) => handleConfigChange('rightPanelParapetPanelType', value)}
          onWidthChange={(value) => handleConfigChange('width', value)}
          onHeightChange={(value) => handleConfigChange('height', value)}
          minWidth={getDoorMinTotalWidth(config.designType)}
          maxWidth={DOOR_MAX_TOTAL_WIDTH}
          maxDoorLeafWidth={DOOR_MAX_DOOR_LEAF_WIDTH}
          minDoorLeafWidth={VOORDEUR_MIN_DOOR_LEAF_WIDTH}
          minSidePanelWidth={VOORDEUR_MIN_SIDE_PANEL_WIDTH}
          minHeight={VOORDEUR_MIN_TOTAL_HEIGHT}
          maxHeight={DOOR_MAX_TOTAL_HEIGHT}
          maxDoorHeight={DOOR_MAX_DOOR_HEIGHT}
          minDoorHeight={DOOR_MIN_DOOR_HEIGHT_MM}
          minOverheadHeight={DOOR_MIN_OVERHEAD_HEIGHT}
        />
      }
      summaryPanel={
        <Summary
          config={config}
          pricing={PRICING}
          profileSystems={profileSystems}
          glassTextures={glassTextures}
          glazingTypes={glazingTypes}
          colors={colors}
          raamtypes={[]}
          hardware={[]}
          accessories={[]}
          woodJointOptions={woodJointOptions}
          partsOptions={[]}
          overheadOptions={[]}
          drawingBoardRef={drawingBoardRef}
          pageType="voordeur"
          onConfigChange={handleConfigChange}
        />
      }
    />
  );
};

export default Voordeur;
